import {defineStore} from "pinia";
import {getStorage, removeStorage, setStorage} from "@/lib/storage";
import {setAuthorizationHeader} from "@/plugin/axios";
import {USER_GROUPS} from "@/modules/Auth/authConst";
import {useLayoutStore} from "@/store/layout";

import authApi from "@/modules/Auth/api/authApi";
import userApi from "@/modules/User/api/userApi";

const ACCESS_TOKEN_STORAGE_NAME = 'access-token'

export const useUserStore = defineStore("user", {
  state: () => ({
    unauthorizedTargetRoute: null,
    user: null,
    phone: null,
    password: null,
    token: '',
    loadingUser: false,
    loadingRefreshToken: false
  }),


  getters: {
    isAuthorization () {
      return Boolean(this.token)
    },
    userAvatar() {
      return this.user?.avatar
    },
    userId() {
      return this.user?.id
    },
    userGroupId() {
      return this.user?.user_group_id
    },
    isClient() {
      return this.userGroupId === USER_GROUPS.CLIENT
    },
    isStaff() {
      return this.userGroupId === USER_GROUPS.STAFF
    },
    finishedTours() {
      return this.user?.finished_tours ?? []
    }
  },

  actions: {
    async login (body) {
      const {access_token: accessToken } = await authApi.login(body)
      setStorage(ACCESS_TOKEN_STORAGE_NAME, accessToken)
      await this.setAccessToken(accessToken)
    },
    async logout () {
      try {
        this.loadingUser = true
        await authApi.logout();
        this.clearToken()
        window.location.href = location.origin
      } finally {
        this.loadingUser = false
      }
    },
    async getUser() {
      try {
        this.loadingUser = true
        this.user = await userApi.getMe({embed: 'avatar'})
      } finally {
        this.loadingUser = false
      }
    },
    async updateUser(user) {
      try {
        this.loadingUser = true
        const innerUser = {...user}
        delete innerUser.phone
        this.user = await userApi.updateItem(this.userId, innerUser)
      } finally {
        this.loadingUser = false
      }
    },
    setUserAvatar(avatar) {
      this.user.avatar = avatar
    },
    deleteUserAvatar() {
      this.user.avatar = null
    },
    async passwordReset (body) {
      this.user = await authApi.passwordReset(body)
    },
    async setAccessToken (accessToken) {
      const layoutStore = useLayoutStore()
      const loadingKey = 'initUser'
      this.token = accessToken
      setAuthorizationHeader(accessToken)

      try {
        layoutStore.showLoading(loadingKey)
        await this.getUser()
      } finally {
        layoutStore.hideLoading(loadingKey)
      }
    },
    clearToken() {
      this.token = ''
      removeStorage(ACCESS_TOKEN_STORAGE_NAME)
    },
    async refreshToken() {
      try {
        this.loadingRefreshToken = true
        const {access_token: accessToken} = await authApi.refreshToken()
        setStorage(ACCESS_TOKEN_STORAGE_NAME, accessToken)
        this.setAccessToken(accessToken)
      } finally {
        this.loadingRefreshToken = false
      }
    },
    async initToken() {
      if(this.token) return
      const accessToken = getStorage(ACCESS_TOKEN_STORAGE_NAME)
      if(!accessToken) return
      await this.setAccessToken(accessToken)
    },
    setDataForActivate ({ phone, password }) {
      this.phone = phone
      this.password = password
    },
    setUnauthorizedTargetRoute(route) {
      this.unauthorizedTargetRoute = route
    },
    clearUnauthorizedTargetRoute() {
      this.setUnauthorizedTargetRoute(null)
    }
  }
})